import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../../Config';

// Icons
import {
  Eye,
  Check,
  X,
  Calendar,
  Truck,
  User,
  Package,
  Search,
  RefreshCw,
  ArrowLeft,
  Edit,
  CheckCircle,
  XCircle,
  Clock,
  Info,
  ChevronDown,
  ChevronUp,
  Tool,
  FileText,
  MessageCircle,
  Building,
  ClipboardCheck,
  Filter
} from 'lucide-react';

const ValidationEvenements = () => {
  const navigate = useNavigate();
  const [evenements, setEvenements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showToast, setShowToast] = useState(false);
  const [validationDialogOpen, setValidationDialogOpen] = useState(false);
  const [selectedEvenement, setSelectedEvenement] = useState(null);
  const [actionType, setActionType] = useState(null);
  const [motifRefus, setMotifRefus] = useState('');
  const [filtreStatut, setFiltreStatut] = useState('en_attente');
  const [searchTerm, setSearchTerm] = useState('');
  const [quantiteEditMode, setQuantiteEditMode] = useState({});
  const [tempQuantites, setTempQuantites] = useState({});
  const [validationMaterielsDialogOpen, setValidationMaterielsDialogOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [expandedRows, setExpandedRows] = useState({});

  useEffect(() => {
    chargerEvenements();
  }, [filtreStatut]);

  const chargerEvenements = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      const response = await axios.get(`${API_URL}/evenements`, config);
      setEvenements(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erreur chargement événements:', error);
      setMessage({
        type: 'error',
        text: error.response?.status === 403 
          ? 'Accès refusé. Vérifiez vos permissions.' 
          : 'Erreur lors du chargement des événements'
      });
      setShowToast(true);
      setEvenements([]);
    } finally {
      setLoading(false);
    }
  };

  const evenementsFiltres = evenements.filter(evenement => {
    if (filtreStatut && evenement.statut !== filtreStatut) return false;
    
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (
        evenement.id.toString().includes(searchLower) ||
        (evenement.beneficiaire?.nom?.toLowerCase().includes(searchLower)) ||
        (evenement.user?.name?.toLowerCase().includes(searchLower)) ||
        (evenement.type?.toLowerCase().includes(searchLower))
      );
    }
    
    return true;
  });

  const paginatedEvenements = evenementsFiltres.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const getStatutBadge = (statut) => {
    switch (statut) {
      case 'valide':
        return (
          <div className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-green-50 text-green-700 border border-green-200 rounded-md">
            <CheckCircle size={12} />
            <span>Validé</span>
          </div>
        );
      case 'refuse':
        return (
          <div className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-red-50 text-red-700 border border-red-200 rounded-md">
            <XCircle size={12} />
            <span>Refusé</span>
          </div>
        );
      case 'en_attente':
      default:
        return (
          <div className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-yellow-50 text-yellow-700 border border-yellow-200 rounded-md">
            <Clock size={12} />
            <span>En attente</span>
          </div>
        );
    }
  };

  const getTypeBadge = (type) => {
    const isEnterrement = type === 'enterrement';
    return (
      <div className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md ${
        isEnterrement 
          ? 'bg-blue-50 text-blue-700 border border-blue-200' 
          : ' text-gray-700 border border-gray-200'
      }`}>
        <Calendar size={12} />
        <span>{isEnterrement ? 'Enterrement' : 'Autre'}</span>
      </div>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }).replace(/\./g, '');
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).replace(/\./g, '');
  };

  const handleOpenValidationDialog = (evenement, action) => {
    setSelectedEvenement(evenement);
    setActionType(action);
    setMotifRefus('');
    setValidationDialogOpen(true);
  };

  const handleOpenValidationMaterielsDialog = (evenement) => {
    setSelectedEvenement(evenement);
    const initialQuantites = {};
    if (evenement.materiels) {
      evenement.materiels.forEach(materiel => {
        const key = `${evenement.id}-${materiel.id}`;
        initialQuantites[key] = materiel.pivot?.quantite_validee || materiel.pivot?.quantite_demandee || 0;
      });
    }
    setTempQuantites(initialQuantites);
    setValidationMaterielsDialogOpen(true);
  };

  const handleCloseValidationDialog = () => {
    setValidationDialogOpen(false);
    setSelectedEvenement(null);
    setActionType(null);
    setMotifRefus('');
  };

  const handleCloseValidationMaterielsDialog = () => {
    setValidationMaterielsDialogOpen(false);
    setSelectedEvenement(null);
    setTempQuantites({});
  };

  const handleValidate = async () => {
    if (!selectedEvenement) return;

    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      };

      const data = {
        statut: actionType,
        ...(actionType === 'refuse' && motifRefus ? { motif_refus: motifRefus } : {})
      };

      await axios.patch(`${API_URL}/evenements/${selectedEvenement.id}`, data, config);
      
      setMessage({
        type: 'success',
        text: actionType === 'valide' ? 'Événement validé avec succès' : 'Événement refusé avec succès'
      });
      setShowToast(true);
      
      chargerEvenements();
    } catch (error) {
      console.error('Erreur validation:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Erreur lors de la validation'
      });
      setShowToast(true);
    } finally {
      handleCloseValidationDialog();
    }
  };

  const handleValidateMateriels = async () => {
    if (!selectedEvenement) return;

    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      };

      const validationPromises = selectedEvenement.materiels.map(async (materiel) => {
        const key = `${selectedEvenement.id}-${materiel.id}`;
        const quantiteValidee = tempQuantites[key];
        
        if (quantiteValidee !== materiel.pivot?.quantite_validee) {
          return axios.patch(
            `${API_URL}/evenements/${selectedEvenement.id}/materiels/${materiel.id}/validation`,
            { quantite_validee: quantiteValidee },
            config
          );
        }
        return Promise.resolve();
      });

      await Promise.all(validationPromises);
      
      setMessage({
        type: 'success',
        text: 'Quantités validées mises à jour avec succès'
      });
      setShowToast(true);
      
      chargerEvenements();
      handleCloseValidationMaterielsDialog();
    } catch (error) {
      console.error('Erreur validation matériels:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Erreur lors de la validation des matériels'
      });
      setShowToast(true);
    }
  };

  const toggleQuantiteEdit = (evenementId, materielId, quantite) => {
    const key = `${evenementId}-${materielId}`;
    setQuantiteEditMode(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
    
    if (!tempQuantites[key]) {
      setTempQuantites(prev => ({
        ...prev,
        [key]: quantite
      }));
    }
  };

  const saveQuantiteValidee = async (evenementId, materielId) => {
    const key = `${evenementId}-${materielId}`;
    const quantiteValidee = tempQuantites[key];

    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      };

      await axios.patch(
        `${API_URL}/evenements/${evenementId}/materiels/${materielId}/validation`,
        { quantite_validee: quantiteValidee },
        config
      );

      setMessage({
        type: 'success',
        text: `Quantité validée mise à jour pour le matériel ${materielId}`
      });
      setShowToast(true);

      setQuantiteEditMode(prev => ({
        ...prev,
        [key]: false
      }));

      chargerEvenements();
    } catch (error) {
      console.error('Erreur mise à jour quantité:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Erreur lors de la mise à jour'
      });
      setShowToast(true);
    }
  };

  const toggleRowExpand = (evenementId) => {
    setExpandedRows(prev => ({
      ...prev,
      [evenementId]: !prev[evenementId]
    }));
  };

  const stats = {
    total: evenements.length,
    enAttente: evenements.filter(e => e.statut === 'en_attente').length,
    valides: evenements.filter(e => e.statut === 'valide').length,
    refuses: evenements.filter(e => e.statut === 'refuse').length
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent mx-auto"></div>
          <p className="mt-3 text-sm text-gray-600">Chargement des événements...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header Simple */}
      <div className="border-b">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-gray-900">Validation Événements</h1>
                <p className="text-sm text-gray-500">{evenements.length} événements</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft size={16} />
              Retour
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-6">
        {/* Quick Stats */}
        <div className="grid grid-cols-4 gap-3 mb-6">
          <div className="text-center">
            <div className="text-2xl font-semibold text-gray-900">{stats.total}</div>
            <div className="text-xs text-gray-500">Total</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-semibold text-yellow-600">{stats.enAttente}</div>
            <div className="text-xs text-gray-500">En attente</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-semibold text-green-600">{stats.valides}</div>
            <div className="text-xs text-gray-500">Validés</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-semibold text-red-600">{stats.refuses}</div>
            <div className="text-xs text-gray-500">Refusés</div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              className="w-full pl-10 pr-4 py-2 text-sm border rounded"
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select
            className="px-3 py-2 text-sm border rounded"
            value={filtreStatut}
            onChange={(e) => setFiltreStatut(e.target.value)}
          >
            <option value="">Tous</option>
            <option value="en_attente">En attente</option>
            <option value="valide">Validés</option>
            <option value="refuse">Refusés</option>
          </select>
          <button
            onClick={chargerEvenements}
            className="p-2 border rounded hover:"
          >
            <RefreshCw size={16} />
          </button>
        </div>

        {/* Main Table */}
        <div className="border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Événement
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Bénéficiaire
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Matériels
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {paginatedEvenements.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500">
                      Aucun événement trouvé
                    </td>
                  </tr>
                ) : (
                  paginatedEvenements.map((evenement) => (
                    <React.Fragment key={evenement.id}>
                      <tr className="hover:">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="font-medium text-gray-900">#{evenement.id}</div>
                            {getTypeBadge(evenement.type)}
                          </div>
                          {evenement.transport && (
                            <div className="flex items-center gap-1 mt-1 text-xs text-blue-600">
                              <Truck size={12} />
                              <span>Transport</span>
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {getStatutBadge(evenement.statut)}
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <div className="text-sm text-gray-900">{evenement.beneficiaire?.nom || 'N/A'}</div>
                            <div className="text-xs text-gray-500">{evenement.user?.nom || 'Client'}</div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Clock size={14} className="text-gray-400" />
                            <span className="text-sm text-gray-600">{formatDate(evenement.created_at)}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Package size={14} className="text-gray-400" />
                            <span className="text-sm text-gray-900">{evenement.materiels?.length || 0}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <button
                              className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                              onClick={() => toggleRowExpand(evenement.id)}
                            >
                              {expandedRows[evenement.id] ? (
                                <ChevronUp size={16} />
                              ) : (
                                <ChevronDown size={16} />
                              )}
                            </button>
                            {evenement.statut === 'en_attente' && (
                              <>
                                <button
                                  className="p-1.5 text-green-600 hover:text-green-700 hover:bg-green-50 rounded"
                                  onClick={() => handleOpenValidationDialog(evenement, 'valide')}
                                >
                                  <Check size={16} />
                                </button>
                                <button
                                  className="p-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded"
                                  onClick={() => handleOpenValidationDialog(evenement, 'refuse')}
                                >
                                  <X size={16} />
                                </button>
                              </>
                            )}
                            {evenement.statut === 'valide' && evenement.materiels && evenement.materiels.length > 0 && (
                              <button
                                className="p-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded"
                                onClick={() => handleOpenValidationMaterielsDialog(evenement)}
                              >
                                <ClipboardCheck size={16} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      
                      {/* Expanded Details */}
                      {expandedRows[evenement.id] && (
                        <tr>
                          <td colSpan={6} className="px-4 py-3 ">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {/* Left Column */}
                              <div className="space-y-4">
                                <div>
                                  <h4 className="text-sm font-medium text-gray-900 mb-2">Informations</h4>
                                  <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Type:</span>
                                      <span className="font-medium">{evenement.type === 'enterrement' ? 'Enterrement' : 'Autre'}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Client:</span>
                                      <span className="font-medium">{evenement.user?.nom || 'N/A'}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Date création:</span>
                                      <span className="font-medium">{formatDateTime(evenement.created_at)}</span>
                                    </div>
                                  </div>
                                </div>
                                
                                {evenement.pack_enterrement && (
                                  <div>
                                    <h4 className="text-sm font-medium text-gray-900 mb-2">Pack</h4>
                                    <div className="text-sm text-gray-600">{evenement.pack_enterrement.nom}</div>
                                  </div>
                                )}
                              </div>
                              
                              {/* Right Column */}
                              <div>
                                <h4 className="text-sm font-medium text-gray-900 mb-2">Matériels</h4>
                                {evenement.materiels && evenement.materiels.length > 0 ? (
                                  <div className="space-y-2">
                                    {evenement.materiels.map((materiel, index) => {
                                      const key = `${evenement.id}-${materiel.id}`;
                                      const isEditing = quantiteEditMode[key];
                                      
                                      return (
                                        <div key={index} className="flex items-center justify-between text-sm p-2 bg-white rounded border">
                                          <div>
                                            <div className="font-medium">{materiel.nom}</div>
                                            <div className="text-xs text-gray-500">Stock: {materiel.stock}</div>
                                          </div>
                                          <div className="flex items-center gap-3">
                                            <div className="text-right">
                                              <div className="text-xs text-gray-500">Dem.</div>
                                              <div className="font-medium text-blue-600">{materiel.pivot?.quantite_demandee || 0}</div>
                                            </div>
                                            <div className="text-right">
                                              <div className="text-xs text-gray-500">Val.</div>
                                              {isEditing ? (
                                                <div className="flex items-center gap-1">
                                                  <input
                                                    type="number"
                                                    className="w-16 px-2 py-1 text-sm border rounded"
                                                    value={tempQuantites[key] || materiel.pivot?.quantite_validee || materiel.pivot?.quantite_demandee || 0}
                                                    onChange={(e) => setTempQuantites(prev => ({
                                                      ...prev,
                                                      [key]: parseInt(e.target.value) || 0
                                                    }))}
                                                    min="0"
                                                    max={materiel.pivot?.quantite_demandee || 0}
                                                  />
                                                  <button
                                                    onClick={() => saveQuantiteValidee(evenement.id, materiel.id)}
                                                    className="p-1 text-green-600"
                                                  >
                                                    <Check size={14} />
                                                  </button>
                                                </div>
                                              ) : (
                                                <div className="flex items-center gap-1">
                                                  <span className={`font-medium ${
                                                    materiel.pivot?.quantite_validee !== null 
                                                      ? 'text-green-600' 
                                                      : 'text-gray-600'
                                                  }`}>
                                                    {materiel.pivot?.quantite_validee || 'Non'}
                                                  </span>
                                                  <button
                                                    onClick={() => toggleQuantiteEdit(evenement.id, materiel.id, materiel.pivot?.quantite_validee)}
                                                    className="p-1 text-gray-400 hover:text-blue-600"
                                                  >
                                                    <Edit size={14} />
                                                  </button>
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                ) : (
                                  <div className="text-sm text-gray-500">Aucun matériel</div>
                                )}
                                
                                {evenement.motif_refus && (
                                  <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded">
                                    <div className="text-sm font-medium text-red-700">Motif de refus:</div>
                                    <div className="text-sm text-red-600">{evenement.motif_refus}</div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          <div className="px-4 py-3 border-t ">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                {paginatedEvenements.length} sur {evenementsFiltres.length}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 0}
                  className="px-3 py-1 text-sm disabled:text-gray-400"
                >
                  ←
                </button>
                <span className="text-sm text-gray-700">Page {page + 1}</span>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={(page + 1) * rowsPerPage >= evenementsFiltres.length}
                  className="px-3 py-1 text-sm disabled:text-gray-400"
                >
                  →
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Validation Modal */}
      {validationDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  {actionType === 'valide' ? 'Valider' : 'Refuser'} l'événement #{selectedEvenement?.id}
                </h3>
                <button
                  onClick={handleCloseValidationDialog}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={20} />
                </button>
              </div>
              
              <div className={`p-4 rounded mb-4 ${
                actionType === 'valide' ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'
              }`}>
                <div className="flex items-center gap-2">
                  {actionType === 'valide' ? <Check size={16} /> : <Info size={16} />}
                  <span className="text-sm">
                    {actionType === 'valide' 
                      ? 'Vous allez valider cet événement.' 
                      : 'Vous allez refuser cet événement.'}
                  </span>
                </div>
              </div>
              
              <div className="space-y-2 text-sm text-gray-600 mb-4">
                <div className="flex justify-between">
                  <span>Type:</span>
                  <span className="font-medium">{selectedEvenement?.type === 'enterrement' ? 'Enterrement' : 'Autre'}</span>
                </div>
                <div className="flex justify-between">
                  <span>Bénéficiaire:</span>
                  <span className="font-medium">{selectedEvenement?.beneficiaire?.nom}</span>
                </div>
                <div className="flex justify-between">
                  <span>Matériels:</span>
                  <span className="font-medium">{selectedEvenement?.materiels?.length || 0}</span>
                </div>
              </div>
              
              {actionType === 'refuse' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Motif du refus
                  </label>
                  <textarea
                    className="w-full px-3 py-2 text-sm border rounded"
                    rows={3}
                    value={motifRefus}
                    onChange={(e) => setMotifRefus(e.target.value)}
                    placeholder="Indiquez le motif du refus..."
                    required
                  />
                </div>
              )}
              
              <div className="flex justify-end gap-3">
                <button
                  onClick={handleCloseValidationDialog}
                  className="px-4 py-2 text-sm text-gray-700 hover: rounded border"
                >
                  Annuler
                </button>
                <button
                  onClick={handleValidate}
                  disabled={actionType === 'refuse' && !motifRefus.trim()}
                  className={`px-4 py-2 text-sm text-white rounded ${
                    actionType === 'valide' 
                      ? 'bg-green-600 hover:bg-green-700 disabled:bg-green-400' 
                      : 'bg-red-600 hover:bg-red-700 disabled:bg-red-400'
                  }`}
                >
                  {actionType === 'valide' ? 'Valider' : 'Refuser'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Materials Validation Modal */}
      {validationMaterielsDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  Validation des matériels - #{selectedEvenement?.id}
                </h3>
                <button
                  onClick={handleCloseValidationMaterielsDialog}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={20} />
                </button>
              </div>
              
              <div className="mb-6">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Info size={16} />
                  <span>Ajustez les quantités validées pour chaque matériel</span>
                </div>
              </div>
              
              {selectedEvenement?.materiels && selectedEvenement.materiels.length > 0 ? (
                <div className="space-y-3">
                  {selectedEvenement.materiels.map((materiel, index) => {
                    const key = `${selectedEvenement.id}-${materiel.id}`;
                    const quantiteDemandee = materiel.pivot?.quantite_demandee || 0;
                    const stockDisponible = materiel.stock || 0;
                    
                    return (
                      <div key={index} className="flex items-center justify-between p-3 border rounded">
                        <div>
                          <div className="font-medium text-sm">{materiel.nom}</div>
                          <div className="text-xs text-gray-500">Stock: {stockDisponible}</div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <div className="text-xs text-gray-500">Demandée</div>
                            <div className="font-medium text-blue-600">{quantiteDemandee}</div>
                          </div>
                          <div className="text-right">
                            <div className="text-xs text-gray-500">Validée</div>
                            <input
                              type="number"
                              className="w-20 px-2 py-1 text-sm border rounded"
                              value={tempQuantites[key] || materiel.pivot?.quantite_validee || quantiteDemandee}
                              onChange={(e) => {
                                const value = parseInt(e.target.value) || 0;
                                const maxValue = Math.min(quantiteDemandee, stockDisponible);
                                setTempQuantites(prev => ({
                                  ...prev,
                                  [key]: Math.min(value, maxValue)
                                }));
                              }}
                              min="0"
                              max={Math.min(quantiteDemandee, stockDisponible)}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  Aucun matériel à valider
                </div>
              )}
              
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={handleCloseValidationMaterielsDialog}
                  className="px-4 py-2 text-sm text-gray-700 hover: rounded border"
                >
                  Annuler
                </button>
                <button
                  onClick={handleValidateMateriels}
                  className="px-4 py-2 text-sm bg-blue-600 text-white hover:bg-blue-700 rounded"
                >
                  Enregistrer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {showToast && (
        <div className="fixed bottom-4 right-4 z-50">
          <div className={`flex items-center gap-2 px-4 py-3 rounded shadow-lg ${
            message.type === 'success' ? 'bg-green-500 text-white' :
            message.type === 'error' ? 'bg-red-500 text-white' :
            'bg-blue-500 text-white'
          }`}>
            {message.type === 'success' ? (
              <Check size={16} />
            ) : message.type === 'error' ? (
              <X size={16} />
            ) : (
              <Info size={16} />
            )}
            <span className="text-sm">{message.text}</span>
            <button
              onClick={() => setShowToast(false)}
              className="ml-2 opacity-80 hover:opacity-100"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ValidationEvenements;